/**
 * webcheck.ts — "Check the web" for an AI draft, using Copyscape Premium (https://www.copyscape.com/api-guide.php).
 *
 * Copyscape searches the public internet for text that matches. Facts from its documentation (read 19 Oct 2026):
 *   • POST https://www.copyscape.com/api/ with u (username), k (API key), o=csearch, e=UTF-8, t (the text), f=json, c, i, l;
 *   • price: 3 cents for the first 200 words + 1 cent per further 100 words (or part); the response states the real "cost";
 *   • c=0..10 asks for full comparisons (per-result percentmatched); allpercentmatched needs c>=3;
 *   • i = comma-separated domains to ignore; l = a dollar spend limit for this one search;
 *   • failures return an "error" text, with no error code.
 * The documentation states no minimum, maximum or rate limit, so the limits below are this CMS's own, kept small on purpose.
 * The three levels (clear / review / copied) are THIS CMS's rule of thumb, not Copyscape's verdict. A person decides.
 */
import { z } from "zod";

export const COPYSCAPE_LIMITS = {
  /** Our own floor: a handful of words cannot be meaningfully compared. */
  minWords: 50,
  /** Our own ceiling: keeps one check under about $0.50 and the response small. */
  maxWords: 4000,
  defaultMonthlyCapUsd: 5,
  maxMonthlyCapUsd: 1000,
  /** Full comparisons requested. 3 is the lowest value that also returns the overall percentage. */
  fullComparisons: 3,
  /** Matches kept per check. */
  maxMatches: 10,
  /** Extra allowance on top of the price estimate, in case comparisons are billed (the docs list no separate charge). */
  spendLimitHeadroomUsd: 0.1,
} as const;

/** What Copyscape's published price says a search of this many words costs, in dollars. */
export function copyscapeCostUsd(words: number): number {
  const w = Math.max(0, Math.floor(words));
  const extra = w > 200 ? Math.ceil((w - 200) / 100) : 0;
  return Math.round((0.03 + 0.01 * extra) * 100) / 100;
}

/** Plain words for the search: markdown marks removed, links keep their text. */
export function markdownToPlainText(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/[*_`~]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function countWords(text: string): number {
  const t = text.trim();
  return t === "" ? 0 : t.split(/\s+/).length;
}

export const WEB_CHECK_LEVELS = ["clear", "review", "copied"] as const;
export const webCheckLevelSchema = z.enum(WEB_CHECK_LEVELS);
export type WebCheckLevel = z.infer<typeof webCheckLevelSchema>;

/** Rule of thumb: any one page matching 30%+ of the draft = copied; any match at all = review; none = clear. */
export const WEB_CHECK_THRESHOLDS = { copiedPercent: 30 } as const;

export function webCheckLevel(matches: ReadonlyArray<{ percentMatched: number | null }>): WebCheckLevel {
  if (matches.length === 0) return "clear";
  const best = Math.max(...matches.map((m) => m.percentMatched ?? 0));
  return best >= WEB_CHECK_THRESHOLDS.copiedPercent ? "copied" : "review";
}

export const webCheckMatchSchema = z.object({
  url: z.string(),
  title: z.string(),
  snippet: z.string(),
  /** Per-page share of the draft's words found on that page; null when Copyscape did not return it. */
  percentMatched: z.number().nullable(),
  wordsMatched: z.number().int().nullable(),
  /** Copyscape's own "at least this many words in a row match" figure. */
  minWordsMatched: z.number().int().nullable(),
});
export type WebCheckMatch = z.infer<typeof webCheckMatchSchema>;

export const webCheckSchema = z.object({
  provider: z.literal("copyscape"),
  checkedAt: z.string(),
  checkedBy: z.string().nullable(),
  words: z.number().int(),
  level: webCheckLevelSchema,
  /** How many pages Copyscape found with matching text (it may be more than are listed). */
  count: z.number().int(),
  /** Overall share of the draft found elsewhere, when Copyscape returned it. */
  allPercentMatched: z.number().nullable(),
  matches: z.array(webCheckMatchSchema),
  /** Real cost reported by Copyscape; null if the response did not say. */
  costUsd: z.number().nullable(),
  /** Domains left out of the search (the site itself and the sources the draft was given). */
  ignoredDomains: z.array(z.string()),
});
export type WebCheck = z.infer<typeof webCheckSchema>;

export const webCheckStatusSchema = z.object({
  configured: z.boolean(),
  month: z.string(),
  spentUsd: z.number(),
  capUsd: z.number(),
  remainingUsd: z.number(),
});
export type WebCheckStatus = z.infer<typeof webCheckStatusSchema>;

/** Bare host from a URL or host string, lower-case, no "www."; null if it is not usable. */
export function hostOf(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  const m = /^(?:[a-z][a-z0-9+.-]*:\/\/)?(?:[^@/\s]*@)?([^/:?#\s]+)(?::\d+)?(?:[/?#]\S*)?$/i.exec(raw);
  const host = (m?.[1] ?? "").toLowerCase().replace(/^www\./, "");
  return /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(host) ? host : null;
}
